import React, { useState } from 'react';
import { Shield, Key, User, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSetupPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    setup_key: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/setup`, formData);
      setSuccess(response.data.message);
      setFormData({ ...formData, password: '', setup_key: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-600 via-black to-red-900 flex items-center justify-center py-12 px-6">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block bg-white p-4 rounded-full mb-4 shadow-2xl">
            <Shield size={48} className="text-red-600" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Admin Setup</h1>
          <p className="text-gray-300">EvalentX Admin Management</p>
        </div>

        {/* Info Box */}
        <div className="bg-blue-900/30 border-2 border-blue-400/50 rounded-xl p-4 mb-6 backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-blue-400 flex-shrink-0 mt-1" size={20} />
            <div className="text-sm text-blue-100">
              <p className="font-semibold mb-1">Security Notice:</p>
              <p>This endpoint requires a setup key from your environment variables. Contact your system administrator for the setup key.</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-2xl p-8 border-2 border-white/20">
          {error && (
            <div className="mb-6 bg-red-500/20 border-2 border-red-400 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 bg-green-500/20 border-2 border-green-400 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="text-green-400 flex-shrink-0" size={20} />
              <div className="text-green-200 text-sm">
                <p className="font-semibold">{success}</p>
                <p className="mt-2">You can now login at <a href="/admin" className="underline font-semibold">Admin Panel</a></p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div>
              <label className="flex items-center gap-2 text-white font-semibold mb-2">
                <User size={18} />
                Admin Username
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/20 border-2 border-white/30 rounded-lg text-white placeholder-gray-300 focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all backdrop-blur-sm"
                placeholder="Enter admin username"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="flex items-center gap-2 text-white font-semibold mb-2">
                <Lock size={18} />
                Admin Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/20 border-2 border-white/30 rounded-lg text-white placeholder-gray-300 focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all backdrop-blur-sm"
                placeholder="Enter strong password"
                required
                minLength="8"
              />
              <p className="text-xs text-gray-300 mt-1">Minimum 8 characters</p>
            </div>

            {/* Setup Key */}
            <div>
              <label className="flex items-center gap-2 text-white font-semibold mb-2">
                <Key size={18} />
                Setup Key
              </label>
              <input
                type="password"
                name="setup_key"
                value={formData.setup_key}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/20 border-2 border-white/30 rounded-lg text-white placeholder-gray-300 focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all backdrop-blur-sm"
                placeholder="Enter setup key"
                required
              />
              <p className="text-xs text-gray-300 mt-1">From ADMIN_SETUP_KEY environment variable</p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg"
            >
              {loading ? (
                <>
                  <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></div>
                  <span>Setting up...</span>
                </>
              ) : (
                <>
                  <Shield size={20} />
                  <span>Setup Admin</span>
                </>
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-300">
              Already have an account?{' '}
              <a href="/admin" className="text-red-400 font-semibold hover:underline">
                Login here
              </a>
            </p>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-black/30 border border-white/20 rounded-lg p-4 backdrop-blur-sm">
          <h3 className="text-white font-semibold mb-2">Alternative Setup Methods:</h3>
          <ul className="text-sm text-gray-300 space-y-1">
            <li>• SSH: <code className="bg-white/10 px-2 py-1 rounded">python admin_manager.py setup</code></li>
            <li>• Or run: <code className="bg-white/10 px-2 py-1 rounded">./quick_admin_setup.sh</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminSetupPage;
